import styled from "styled-components"
import React, { FC } from "react"
import Helpers from "../../../../utils/Helpers"
import { Container, H3 } from "@carrotfertility/carotene"
import { Body, BodyLargeAlt } from "../../atoms/Bodies"
import { SupportCenterLinks } from "../../../../utils/SupportCenterLinks"
import { CMD_CONFIGURATION, useUserDeductible } from "../../../context/user/DeductibleContext"
import { ZendeskArrowLink } from "../../atoms/links/ZendeskArrowLink"
import { getPiiProps } from "services/tracking"
import { useIntl } from "react-intl"

type CMDRowProps = {
  name: string
  value: string
}

type CMDHeaderProps = {
  startDate: string
  endDate: string
}

type CMDTableProps = {
  title: string
  startDate: string
  endDate: string
  totalAmount: number
  cappedExternalCredit: number
  cappedCarrotAmountApplied: number
  amountRemaining: number
}

const CMDTableParent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  margin-top: 8px;
  width: 100%;
  background: #faf9f7;
  border-radius: 8px;
`

const CMDTableChild = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  margin: 0px 10px;
`

const CMDHeaderParent = styled.div`
  left: 0px;
  top: 0px;
  width: 100%;
  padding-bottom: 15px;
`

const CMDRowParent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  @media (max-width: 525px) {
    height: 100%;
  }
`

const CMDDivider = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0;
  border-top: 1px solid #cccccc;
  @media (max-width: 525px) {
    height: 100%;
  }
`

const CMDRowChild = styled.div`
  left: 0px;
  top: 41px;
  font-family: Gotham;
  font-style: normal;
  font-weight: normal;
  line-height: 150%;
  color: #000000;
  @media (max-width: 525px) {
    height: 100%;
    margin-top: 1px;
    margin-right: 5px;
  }
`

const CMDRowValue = styled.div`
  display: flex;
  align-items: flex-start;
  width: 50%;
  line-height: 150%;
  margin: 0 5px;
  @media (max-width: 525px) {
    height: 100%;
    margin: 0 5px;
  }
`

const CMDHeader: FC<CMDHeaderProps> = ({ startDate, endDate }) => {
  const intl = useIntl()
  const formattedStartDate = intl.formatDate(startDate, { month: "short", day: "numeric", year: "numeric" })
  const formattedEndDate = intl.formatDate(endDate, { month: "short", day: "numeric", year: "numeric" })
  const planYear = intl.formatMessage(
    { defaultMessage: "Plan year: {startDate} - {endDate}" },
    { startDate: formattedStartDate, endDate: formattedEndDate }
  )
  return (
    <CMDHeaderParent>
      <H3>{planYear}</H3>
    </CMDHeaderParent>
  )
}

const CMDRow: FC<CMDRowProps> = ({ name, value }) => (
  <CMDRowParent>
    <CMDRowChild style={{ width: "67%" }}>
      <Body color={"black-80"}>{name}</Body>
    </CMDRowChild>
    <CMDRowValue {...getPiiProps()} style={{ width: "33%" }}>
      <BodyLargeAlt color={"black-80"}>{value}</BodyLargeAlt>
    </CMDRowValue>
  </CMDRowParent>
)

const CMDTable: FC<CMDTableProps> = ({
  title,
  startDate,
  endDate,
  totalAmount,
  cappedExternalCredit,
  cappedCarrotAmountApplied,
  amountRemaining
}) => {
  const intl = useIntl()
  return (
    <>
      <Container padding="none" stack="medium">
        <H3>{title}</H3>
        <CMDTableParent>
          <CMDTableChild>
            <CMDHeader startDate={startDate} endDate={endDate} />
            <CMDRow
              name={intl.formatMessage({ defaultMessage: "Annual deductible" })}
              value={intl.formatNumber(totalAmount / 100, {
                style: "currency",
                currency: "USD",
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 0
              })}
            />
            <CMDRow
              name={intl.formatMessage({ defaultMessage: "Credit from health plan" })}
              value={
                (cappedExternalCredit > 0 ? "-" : "") +
                intl.formatNumber(cappedExternalCredit / 100, {
                  style: "currency",
                  currency: "USD",
                  currencyDisplay: "narrowSymbol",
                  maximumFractionDigits: 0
                })
              }
            />
            <CMDRow
              name={intl.formatMessage({ defaultMessage: "Credit from applicable expenses" })}
              value={
                (cappedCarrotAmountApplied > 0 ? "-" : "") +
                intl.formatNumber(cappedCarrotAmountApplied / 100, {
                  style: "currency",
                  currency: "USD",
                  currencyDisplay: "narrowSymbol",
                  maximumFractionDigits: 0
                })
              }
            />
            <CMDDivider />
            <CMDRow
              name={intl.formatMessage({ defaultMessage: "Remaining deductible" })}
              value={intl.formatNumber(amountRemaining / 100, {
                style: "currency",
                currency: "USD",
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 0
              })}
            />
          </CMDTableChild>
        </CMDTableParent>
      </Container>
    </>
  )
}

function useMemberCMDTableSetup() {
  const {
    deductible: { deductibleSnapshot: memberDeductibleSnapshot }
  } = useUserDeductible()
  const startDateMember = Helpers.formatDate(memberDeductibleSnapshot.planStartDate)
  const endDateMember = Helpers.formatDate(memberDeductibleSnapshot.planEndDate)
  const externalCreditCapMember = Math.max(
    0,
    memberDeductibleSnapshot.totalAmount - memberDeductibleSnapshot.carrotAmountAppliedToDeductible
  )
  const cappedExternalCreditMember = Math.min(
    externalCreditCapMember,
    memberDeductibleSnapshot.externalAmountAppliedToDeductible
  )
  const cappedCarrotAmountAppliedMember = Math.min(
    memberDeductibleSnapshot.totalAmount,
    memberDeductibleSnapshot.carrotAmountAppliedToDeductible
  )
  return {
    memberDeductibleSnapshot,
    startDateMember,
    endDateMember,
    cappedExternalCreditMember,
    cappedCarrotAmountAppliedMember
  }
}

function usePartnerCMDTableSetup() {
  const {
    partnerDeductible: { deductibleSnapshot: partnerDeductibleSnapshot }
  } = useUserDeductible()
  const startDatePartner = Helpers.formatDate(partnerDeductibleSnapshot.planStartDate)
  const endDatePartner = Helpers.formatDate(partnerDeductibleSnapshot.planEndDate)
  const externalCreditCapPartner = Math.max(
    0,
    partnerDeductibleSnapshot.totalAmount - partnerDeductibleSnapshot.amountAppliedToDeductible
  )
  const cappedExternalCreditPartner = Math.min(
    externalCreditCapPartner,
    partnerDeductibleSnapshot.externalAmountAppliedToDeductible
  )
  const cappedCarrotAmountAppliedPartner = Math.min(
    partnerDeductibleSnapshot.totalAmount,
    partnerDeductibleSnapshot.carrotAmountAppliedToDeductible
  )
  return {
    partnerDeductibleSnapshot,
    startDatePartner,
    endDatePartner,
    cappedExternalCreditPartner,
    cappedCarrotAmountAppliedPartner
  }
}

function CMDTableMember(): JSX.Element {
  const {
    memberDeductibleSnapshot,
    startDateMember,
    endDateMember,
    cappedExternalCreditMember,
    cappedCarrotAmountAppliedMember
  } = useMemberCMDTableSetup()
  const intl = useIntl()

  return (
    <CMDTable
      title={intl.formatMessage({ defaultMessage: "Your deductible status" })}
      startDate={startDateMember}
      endDate={endDateMember}
      totalAmount={memberDeductibleSnapshot.totalAmount}
      cappedExternalCredit={cappedExternalCreditMember}
      cappedCarrotAmountApplied={cappedCarrotAmountAppliedMember}
      amountRemaining={memberDeductibleSnapshot.amountRemaining}
    />
  )
}

function CMDTablePartner(): JSX.Element {
  const {
    partnerDeductibleSnapshot,
    startDatePartner,
    endDatePartner,
    cappedExternalCreditPartner,
    cappedCarrotAmountAppliedPartner
  } = usePartnerCMDTableSetup()
  const intl = useIntl()

  return (
    <CMDTable
      title={intl.formatMessage({ defaultMessage: "Your partner's deductible status" })}
      startDate={startDatePartner}
      endDate={endDatePartner}
      totalAmount={partnerDeductibleSnapshot.totalAmount}
      cappedExternalCredit={cappedExternalCreditPartner}
      cappedCarrotAmountApplied={cappedCarrotAmountAppliedPartner}
      amountRemaining={partnerDeductibleSnapshot.amountRemaining}
    />
  )
}

function CMDTableContainer(): JSX.Element {
  const { cmdConfiguration, hasCMD } = useUserDeductible()
  const hasLegacyCMD = hasCMD && cmdConfiguration === null
  const shouldShowMemberTable =
    [
      CMD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
      CMD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED,
      CMD_CONFIGURATION.MEMBER_ONLY
    ].includes(cmdConfiguration) || hasLegacyCMD
  const shouldShowPartnerTable = [
    CMD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
    CMD_CONFIGURATION.PARTNER_ONLY
  ].includes(cmdConfiguration)
  const intl = useIntl()
  return (
    <>
      {shouldShowMemberTable && <CMDTableMember />}
      {shouldShowPartnerTable && <CMDTablePartner />}
      {hasCMD && (
        <ZendeskArrowLink supportCenterLink={SupportCenterLinks.WHY_HAVE_DEDUCTIBLE_MANAGED_BY_CARROT}>
          {intl.formatMessage({ defaultMessage: "Learn more about this deductible" })}
        </ZendeskArrowLink>
      )}
    </>
  )
}

export { CMDTableContainer }
